import { memo, ReactElement, useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import cc from 'classcat';
import dayjs from 'dayjs';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import { EIconSize } from 'utils/constants/app/ui';
import { sports as SPORTS_WHAT } from 'utils/constants/sportsbook/data-fields';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { isMobile } from 'utils/is-mobile';
import { getLivePreMatchSportTypes } from 'utils/sportsbook/sport-data';
import RidGenerator from 'utils/swarm/rid-generator';
import { EPageTypes } from 'interfaces/sportsbook';
import {
  Sport,
  SportOnly,
  Sport as TSport
} from 'interfaces/sportsbook-data-levels';
import { executeCommand, unsubscribeCommand } from 'services/get-data-level';
import { GlobalIcon } from 'components/GlobalIcon';
import { Option, Select } from 'newcomponents/UI/Select';
import { Skeleton } from 'newcomponents/UI/Skeleton';
import { useMounted } from 'hooks/useMounted';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import './index.less';

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

type TProps = {
  pageType: EPageTypes.prematch | EPageTypes.live;
  extraClass?: string;
  groupSports?: (SportOnly | Sport)[] | undefined;
  drawAlternativeSkeleton?: boolean;
  openDirectionBottom?: boolean;
};

export const SportSelect = memo(
  ({
    pageType,
    extraClass,
    groupSports,
    drawAlternativeSkeleton,
    openDirectionBottom
  }: TProps): ReactElement => {
    const [sports, setSports] = useState<TSport[] | typeof groupSports | null>(
      null
    );

    const location = useLocation();
    const history = useHistory();

    const { mounted } = useMounted();
    const { params } = useSbRouteMatch();

    const handleSelect = useCallback(
      (sportAlias: string) => {
        if (!params.sport) {
          const { pathname } = location;

          const separator = pathname[pathname.length - 1] === '/' ? '' : '/';

          history.push(
            `${pathname}${separator}${sportAlias}${window.location.search}`
          );

          return;
        }

        if (params.sport !== sportAlias) {
          history.push(
            `${location.pathname.replace(params.sport, sportAlias)}${
              window.location.search
            }`
          );
        }
      },
      [location.pathname, params.sport]
    );

    useEffect(() => {
      if (groupSports) {
        setSports(groupSports);

        return;
      }

      const rid = RidGenerator.gForSubscribe();

      executeCommand(
        rid,
        { ...SPORTS_WHAT, game: '@count' },
        {
          game: {
            start_ts: {
              '@gt': dayjs().unix(),
              '@lt': dayjs().endOf('day').unix()
            }
          },
          market: {
            display_key: 'WINNER',
            display_sub_key: 'MATCH'
          },
          sport: {
            type: {
              '@in': getLivePreMatchSportTypes(pageType)
            }
          }
        },
        (data: { sport: Record<string, TSport> }) => {
          if (mounted.current) {
            const sports = sortByOrder(Object.values(data?.sport || {}));

            setSports(sports);

            if (!params.sport) {
              handleSelect(sports[0].alias);
            }
          }
        },
        true
      );

      return () => {
        unsubscribeCommand(rid);
      };
    }, []);

    return (
      <div className={cc(['sport-select-wrapper', extraClass])}>
        {!sports || !params ? (
          drawAlternativeSkeleton ? (
            <div
              className={cc([
                'sport-select-wrapper__header',
                {
                  'sport-select-wrapper__header-mobile': isMobile()
                }
              ])}
            >
              <span
                className={cc([
                  'sport-select-wrapper__header--title',
                  {
                    'sport-select-wrapper__header--title-mobile': isMobile()
                  }
                ])}
              >
                <Skeleton.Button active />
              </span>

              <div className="headerRightSkeletonWrapper">
                <span className="sortingBtn sortingBtn-skeleton">
                  <Skeleton.Button active />
                </span>
              </div>
            </div>
          ) : (
            <Skeleton.Button active />
          )
        ) : (
          <Select
            suffixIcon={
              <GlobalIcon
                lib="generic"
                name="caretDown"
                theme="default"
                size={EIconSize._12}
              />
            }
            value={params.sport}
            className="sport-select-wrapper__select"
            dropdownClassName="sport-select-wrapper__dropdown"
            getPopupContainer={trigger => trigger.parentNode}
            onChange={sportAlias => handleSelect(sportAlias)}
            filterOption={(input, option) =>
              `${option?.children}`.toLowerCase().includes(input.toLowerCase())
            }
            openDirectionBottom={openDirectionBottom}
          >
            {
              sports.map(({ id, alias, name, game }) => {
                return (
                  <Option value={alias} key={id}>
                    <div className="sport-select-wrapper__option">
                      <GlobalIcon
                        lib="sports"
                        theme="colored"
                        name={alias}
                        size={EIconSize._24}
                        className="sport-select-wrapper__option-icon"
                      />
                      <span className="sport-select-wrapper__option-title text-color">
                        {name} ({typeof game !== 'object' ? game : ''})
                      </span>
                    </div>
                  </Option>
                );
              }) as ReactElement[]
            }
          </Select>
        )}
      </div>
    );
  }
);
